import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { SmoothScroll } from "../components/SmoothScroll";
import { Eyebrow, MaskLines, Reveal } from "../components/Motion";
import { BuildCards } from "../components/gaming/BuildCards";
import { BuildForm } from "../components/gaming/BuildForm";
import { PcBuilder } from "../components/gaming/PcBuilder";

const empty = { budget: "", resolution: "", uses: ["Gaming"], games: "", starting_build: "", extras: [], notes: "", name: "", email: "", phone: "", preferred_contact: "email", parts: {}, partsSummary: null };

const steps = [
  ["01", "Tell us your games", "Budget, resolution and what you play."],
  ["02", "Get a parts list", "We pick parts from current Amazon stock and send you a quote."],
  ["03", "Built & tested", "Assembled, cable-managed and stress-tested before it leaves us."],
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  return (
    <section ref={ref} data-testid="gaming-hero" className="grain grid-lines relative overflow-hidden pt-28 md:pt-32">
      <div className="mx-auto grid max-w-[1440px] gap-10 px-5 pb-16 md:px-10 lg:grid-cols-12 lg:pb-24">
        <div className="lg:col-span-7 lg:pt-8">
          <Eyebrow>Custom gaming PCs</Eyebrow>
          <MaskLines onLoad className="mt-6 font-display font-black uppercase leading-[0.86] tracking-[-0.03em] text-[clamp(3.2rem,9vw,8.5rem)]"
            lines={["Built for", <span key="b" className="text-outline">your</span>, <span key="c">game<span className="text-ember">.</span></span>]} />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.9 }} className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
            Custom gaming computers built by Wilko Tech LTD from parts in current Amazon stock. Tell us your budget and games, and we'll quote you a build.
          </motion.p>
          <motion.a initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} href="#build-quote" data-testid="gaming-hero-quote-btn"
            className="group mt-8 inline-flex items-center gap-3 bg-ember px-7 py-4 font-semibold text-ink transition-colors hover:bg-amber-400">
            Get a build quote <ArrowDown className="h-5 w-5 transition-transform group-hover:translate-y-1" />
          </motion.a>
        </div>
        <motion.div style={{ y }} className="lg:col-span-5">
          <motion.div initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            className="clip-notch relative aspect-[4/5] overflow-hidden">
            <img src="/img/gaming-tower.jpg" alt="Custom gaming PC with blue fans" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default function GamingPC() {
  const [f, setF] = useState(empty);
  const pick = (name) => {
    setF((p) => ({ ...p, starting_build: name }));
    document.getElementById("build-quote")?.scrollIntoView({ behavior: "smooth" });
  };
  const sendBuild = (s) => {
    setF((p) => ({ ...p, parts: s.parts, partsSummary: s, starting_build: "" }));
    document.getElementById("build-quote")?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <SmoothScroll>
      <Nav />
      <main>
        <Hero />
        <section className="border-y border-white/[0.06] bg-slab/40">
          <div className="mx-auto grid max-w-[1440px] gap-px px-5 md:grid-cols-3 md:px-10">
            {steps.map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.08} className="py-8 md:px-6">
                <p className="font-mono text-sm text-ember">{n}</p>
                <h3 className="mt-2 font-display text-2xl font-extrabold">{t}</h3>
                <p className="mt-2 text-sm text-slate-300">{d}</p>
              </Reveal>
            ))}
          </div>
        </section>
        <section className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32" data-testid="example-builds-section">
          <div className="mb-12 grid gap-6 md:grid-cols-12 md:items-end">
            <div className="md:col-span-7">
              <Eyebrow>Example builds</Eyebrow>
              <MaskLines className="mt-5 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl lg:text-7xl" lines={["Pick a starting", "point."]} />
            </div>
            <p className="text-slate-300 md:col-span-5">Exact parts depend on what's in stock on Amazon at the time, so every build is quoted individually.</p>
          </div>
          <BuildCards onPick={pick} />
        </section>
        <section id="pc-builder" className="border-t border-white/[0.06] bg-slab/30" data-testid="pc-builder-section">
          <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-32">
            <div className="mb-12 grid gap-6 md:grid-cols-12 md:items-end">
              <div className="md:col-span-7">
                <Eyebrow>Build your own</Eyebrow>
                <MaskLines className="mt-5 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl lg:text-7xl" lines={["Pick every", "part."]} />
              </div>
              <p className="text-slate-300 md:col-span-5">Choose each part from the drop-downs and get a rough estimate based on Amazon UK prices. Send it to us and we'll confirm the final quote.</p>
            </div>
            <PcBuilder onSend={sendBuild} />
          </div>
        </section>
        <section id="build-quote" className="scroll-mt-24 border-t border-white/[0.06]" data-testid="build-quote-section">
          <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-24 md:px-10 md:py-32 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Eyebrow>Build quote</Eyebrow>
              <MaskLines className="mt-5 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl" lines={["Spec your", "machine."]} />
              <p className="mt-6 max-w-sm text-slate-300">You'll get a WT- reference number to track your build, and we'll contact you with a parts list and quote.</p>
            </div>
            <div className="lg:col-span-8"><BuildForm f={f} setF={setF} /></div>
          </div>
        </section>
      </main>
      <Footer />
    </SmoothScroll>
  );
}
